import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { computeSliceShift } from '../../utils/sliceShift';
import { positionFor } from '../../utils/anchoredNav';
import type { UploadedBatch } from '../../utils/uploadedBatch';
import { applySideXClip, newSideXClip, type SideXClip } from '../../utils/sideXClip';

// ---------------------------------------------------------------------------
// Pontas contínuas da lateral (src/utils/sideXClip.ts): a cada quadro escreve a janela visível em X nos uniforms do
// shader de cada linha da lateral. A janela é a mesma do recorte por drawRange (deslocamento do lote que está na GPU,
// descritor C1): lote da parede para a linha principal, lote principal para as SMAs.
// ---------------------------------------------------------------------------

type LineRef = { current: THREE.Line | null };
export interface SideXClipArgs {
  lines: LineRef[];                       // 0 lateral (lote da parede), 1-3 SMAs da lateral (lote principal)
  drawRef: { current: UploadedBatch | null };
  historyOffsetRef: { current: number } | undefined;
  startX: number; backChartWidth: number;
  enabled: boolean;
}

export function useSideXClip(a: SideXClipArgs): void {
  const st = useRef<SideXClip[] | null>(null);
  if (st.current === null) st.current = [newSideXClip(), newSideXClip(), newSideXClip(), newSideXClip()];
  const win = useRef(new Float64Array(4));          // [lo, hi] da parede, [lo, hi] do lote principal
  useFrame(() => {
    const U = a.drawRef.current;
    if (!U || !a.enabled || !a.historyOffsetRef) return;
    const W = win.current, S = st.current!;
    // posição relativa a cada lote (F2): a mesma janela do recorte por drawRange em SceneContent
    if (U.wallSmoothedPtsCount > 1) {
      const sh = computeSliceShift(positionFor(a.historyOffsetRef, U.wallNewestAbs), U.wallSliceOffset, U.wallPadLeft, U.wallPadRight) * U.wallStepX;
      W[0] = a.startX - sh; W[1] = a.startX + a.backChartWidth - sh;
    } else { W[0] = -1e30; W[1] = 1e30; }                // sem pontos suavizados: sem corte (como o drawRange)
    const shM = computeSliceShift(positionFor(a.historyOffsetRef, U.newestAbs), U.sliceOffset, U.padLeft, U.padRight) * U.stepX;
    W[2] = a.startX - shM; W[3] = a.startX + a.backChartWidth - shM;
    applySideXClip(a.lines[0].current, S[0], W, 0);
    for (let s = 1; s < 4; s++) applySideXClip(a.lines[s].current, S[s], W, 2);
  });
}
